import React, { useEffect, useState } from 'react';
import { Plane, PlaneTakeoff, PlaneLanding, TrendingDown, MapPin, Calendar, ArrowRight } from 'lucide-react';
import service, { getImageUrl } from '../../Service/Service';
import { useNavigate } from 'react-router-dom';
import PriceDisplay from '../PriceDisplay';

/** Stable component so cards don't remount on parent re-render (avoids price blink on scroll). Fixed heights so all cards match (with or without image/logo). */
const DealCard = ({ deal, onClick }) => (
  <div
    onClick={onClick}
    className="group cursor-pointer bg-white rounded-xl shadow-md md:hover:shadow-lg transition-all duration-300 overflow-hidden transform md:hover:-translate-y-1 relative flex flex-col h-full min-h-[320px]"
  >
    {/* Image area: always reserve same height so cards align */}
    <div className="relative h-40 shrink-0 overflow-hidden bg-gray-100">
      {deal.imagePath ? (
        <img
          src={getImageUrl(deal.imagePath)}
          alt={`${deal.destination_city || deal.destination} destination`}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ) : null}
    </div>

    <div className="bg-gradient-to-r from-secondary to-navy-800 p-4 text-white shrink-0">
      <div className="flex items-center mb-1">
        <h3 className="text-2xl font-extrabold tracking-tight text-left truncate">
          {deal.destination_city || deal.destination}
        </h3>
      </div>
    </div>

    <div className="p-4 flex-1 flex flex-col min-h-0">
      {/* Date + airline row: fixed min-height so cards with/without logo match */}
      <div className="space-y-2 mb-3 min-h-[52px]">
        <div className="flex items-center justify-between gap-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <PlaneTakeoff className="w-4 h-4 shrink-0" />
            <span>
              {deal.departureDate
                ? `Depart: ${new Date(deal.departureDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                : 'Depart: —'}
            </span>
          </div>
          <div className="flex items-center justify-end w-8 h-8 shrink-0">
            {deal.airlineCode ? (
              <img
                src={`https://www.gstatic.com/flights/airline_logos/70px/${deal.airlineCode}.png`}
                alt={deal.airlineName || deal.airlineCode}
                title={deal.airlineName || deal.airlineCode}
                className="w-8 h-8 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const planeIcon = document.createElement('div');
                  planeIcon.innerHTML =
                    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3L4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"></path></svg>';
                  e.target.parentElement.insertAdjacentHTML(
                    'beforeend',
                    planeIcon.innerHTML,
                  );
                }}
              />
            ) : (
              <Plane className="w-5 h-5 text-gray-500" />
            )}
          </div>
        </div>
        {deal.returnDate && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <PlaneLanding className="w-4 h-4 shrink-0" />
            <span>
              Return:{' '}
              {new Date(deal.returnDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
              })}
            </span>
          </div>
        )}
      </div>

      <div className="mt-2">
        <div className="flex items-baseline gap-2">
          <PriceDisplay
            amount={deal.price.total}
            displayCurrency="GBP"
            className="text-3xl font-bold text-secondary"
          />
        </div>
      </div>
    </div>

    <div className="absolute top-2 right-2">
      <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full shadow-lg">
        HOT DEAL
      </span>
    </div>
  </div>
);

const DealsSection = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(null);
  const [availableCities, setAvailableCities] = useState([]);
  const [citySections, setCitySections] = useState([]); // [{ city: { code, name }, deals }] for mobile vertical layout
  const [searchingFlights, setSearchingFlights] = useState(false);
  const [searchProgress, setSearchProgress] = useState('');
  const navigate = useNavigate();

  const normalizeDeal = (deal) => ({
    origin: deal.origin_code,
    origin_city: deal.origin_city,
    destination: deal.destination_code,
    destination_city: deal.destination_city,
    departureDate: deal.departure_date,
    returnDate: deal.return_date,
    price: {
      total: deal.cached_price ? deal.cached_price.toString() : '0',
      currency: deal.cached_currency || 'GBP'
    },
    imagePath: deal.image_path,
    airlineCode: deal.airline_code,
    airlineName: deal.airline_name,
    seo_slug: deal.seo_slug
  });

  // Fetch all thumbnail deals first to get available cities (and for mobile: all deals grouped by city)
  const fetchAvailableCities = async () => {
    try {
      const response = await service.getThumbnailDeals({});
      
      if (response.data && response.data.success && response.data.deals && response.data.deals.length > 0) {
        const rawDeals = response.data.deals;
        // Group by origin for mobile vertical sections
        const byOrigin = new Map();
        rawDeals.forEach(deal => {
          if (!deal.origin_code || !deal.origin_city) return;
          if (!byOrigin.has(deal.origin_code)) {
            byOrigin.set(deal.origin_code, { city: { code: deal.origin_code, name: deal.origin_city }, deals: [] });
          }
          byOrigin.get(deal.origin_code).deals.push(deal);
        });
        const sections = Array.from(byOrigin.values()).map(({ city, deals }) => ({
          city,
          deals: deals.map(normalizeDeal).slice(0, 8)
        }));
        setCitySections(sections);

        // Extract unique origin cities for desktop selector
        const uniqueCities = Array.from(byOrigin.keys()).map(code => {
          const { city } = byOrigin.get(code);
          return { code: city.code, name: city.name };
        });
        setAvailableCities(uniqueCities);
        
        // Set first available city as selected and load its deals for desktop
        if (uniqueCities.length > 0 && !selectedCity) {
          setSelectedCity(uniqueCities[0].code);
          fetchDeals(uniqueCities[0].code);
        }
        
        console.log(`✅ Found ${uniqueCities.length} cities with thumbnail deals:`, uniqueCities);
      } else {
        setAvailableCities([]);
        setCitySections([]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch available cities:', error);
      setAvailableCities([]);
      setCitySections([]);
      setLoading(false);
    }
  };

  const fetchDeals = async (origin) => {
    setLoading(true);
    try {
      // Fetch only thumbnail deals (for Today's Best Deals section)
      const response = await service.getThumbnailDeals({
        origin: origin
      });
      
      if (response.data && response.data.success && response.data.deals && response.data.deals.length > 0) {
        // Use pre-computed deals from database
        const activeDeals = response.data.deals.map(deal => ({
          origin: deal.origin_code,
          origin_city: deal.origin_city,
          destination: deal.destination_code,
          destination_city: deal.destination_city,
          departureDate: deal.departure_date,
          returnDate: deal.return_date,
          price: {
            total: deal.cached_price ? deal.cached_price.toString() : '0',
            currency: deal.cached_currency || 'GBP'
          },
          imagePath: deal.image_path,
          airlineCode: deal.airline_code,
          airlineName: deal.airline_name,
          seo_slug: deal.seo_slug // Include SEO slug for navigation
        }));
        
        setDeals(activeDeals.slice(0, 8)); // Show max 8 deals
        console.log(`✅ Loaded ${activeDeals.length} deals from database for ${origin}`);
      } else {
        // No deals configured - show nothing
        console.log(`ℹ️ No deals found in database for ${origin}`);
        setDeals([]);
      }
    } catch (error) {
      console.error('Failed to fetch deals:', error);
      // On error - show nothing
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // First fetch available cities, then fetch deals for selected city
    fetchAvailableCities();
  }, []);

  useEffect(() => {
    // Fetch deals when selectedCity changes
    if (selectedCity) {
      fetchDeals(selectedCity);
    }
  }, [selectedCity]);

  const handleDealClick = async (deal) => {
    // If deal has a seo_slug, navigate to the deal landing page
    if (deal.seo_slug) {
      console.log('🎯 Navigating to deal landing page:', deal.seo_slug);
      navigate(`/deals/${deal.seo_slug}`);
      return;
    }
    
    // Fallback: If no seo_slug, use the old behavior (search flights)
    console.log('🎯 Searching flights for deal (no seo_slug):', {
      origin: deal.origin,
      destination: deal.destination,
      departureDate: deal.departureDate,
      returnDate: deal.returnDate
    });
    
    setSearchingFlights(true);
    setSearchProgress('Preparing search...');
    
    const bookingData = {
      departure: deal.origin,
      arrival: deal.destination,
      departure_name: deal.origin_city ? `${deal.origin_city} (${deal.origin})` : `${getCityName(deal.origin)} (${deal.origin})`,
      arrival_name: deal.destination_city ? `${deal.destination_city} (${deal.destination})` : deal.destination,
      date: deal.departureDate,
      return_date: deal.returnDate || null,
      adults: '1',
      children: '0',
      infants: '0',
      flight_type: 'return',
      flight_class: 'economy'
    };
    
    try {
      // Search Duffel and Amadeus in parallel
      setSearchProgress('Searching Duffel flights...');
      const duffelPromise = service.postGetFlightOfferDuffel({
        arrival: deal.destination,
        departure: deal.origin,
        date: deal.departureDate,
        return_date: deal.returnDate || '',
        adults: '1',
        children: '0',
        infants: '0',
        flight_type: 'return',
        flight_class: 'economy'
      });
      
      setSearchProgress('Searching Amadeus flights...');
      const amadeusPromise = service.postGetFlights({
        arrival: deal.destination,
        departure: deal.origin,
        date: deal.departureDate,
        return_date: deal.returnDate || '',
        adult: '1'
      });
      
      setSearchProgress('Comparing prices...');
      const [duffelResponse, amadeusResponse] = await Promise.all([duffelPromise, amadeusPromise]);
      
      console.log('✅ Duffel results:', duffelResponse.data);
      console.log('✅ Amadeus results:', amadeusResponse.data);
      
      const combinedOffers = {
        oneway: {
          duffel: duffelResponse.data?.oneway?.offers || [],
          amadeus: amadeusResponse.data?.oneway || [],
          flightlabs: []
        },
        return: {
          duffel: duffelResponse.data?.return?.offers || [],
          amadeus: amadeusResponse.data?.return || [],
          flightlabs: []
        }
      };
      
      const duffelCount = (combinedOffers.oneway.duffel?.length || 0);
      const amadeusCount = (combinedOffers.oneway.amadeus?.length || 0);
      console.log(`📦 Combined offers: ${duffelCount} Duffel + ${amadeusCount} Amadeus = ${duffelCount + amadeusCount} total flights`);
      console.log('📦 Combined structure:', JSON.stringify(combinedOffers, null, 2));
      
      localStorage.setItem('offer', JSON.stringify(combinedOffers));
      
      // Store booking data
      localStorage.setItem('booking', JSON.stringify(bookingData));
      
      setSearchProgress('Flights found! Redirecting...');
      
      // Small delay before navigation for better UX
      setTimeout(() => {
        setSearchingFlights(false);
        navigate('/flight-detail-search?package=0');
      }, 500);
      
    } catch (error) {
      console.error('Error searching flights:', error);
      setSearchProgress('Error searching flights. Please try again.');
      setTimeout(() => {
        setSearchingFlights(false);
        // Navigate anyway with the booking data
        localStorage.setItem('booking', JSON.stringify(bookingData));
        localStorage.removeItem('offer');
        navigate('/flight-detail-search?package=0');
      }, 2000);
    }
  };

  const getCityName = (code) => {
    const city = availableCities.find(c => c.code === code);
    return city ? city.name : code;
  };

  // Show loading spinner only on initial load
  if (loading && deals.length === 0 && availableCities.length === 0) {
    return (
      <div className="my-[70px] w-[90%] md:w-[80%] mx-auto">
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
        </div>
      </div>
    );
  }

  // Hide entire section if no deals configured
  if (!loading && availableCities.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 mb-6 w-full md:w-[80%] mx-0 md:mx-auto overflow-x-hidden md:overflow-visible">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-6">
          <TrendingDown className="text-secondary w-8 h-8" />
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Today's Best Deals
          </h2>
        </div>
        
        {/* City Selector - Only on tablet/desktop; mobile shows vertical From (City) + deals */}
        {availableCities.length > 0 && (
          <div className="hidden md:flex flex-wrap items-center justify-center gap-2">
            {availableCities.map((city) => (
              <button
                key={city.code}
                onClick={() => {
                  setSelectedCity(city.code);
                }}
                className={`px-2.5 py-1 rounded-md font-medium text-sm transition-all ${
                  selectedCity === city.code
                    ? 'bg-secondary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {city.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Deals Grid */}
      {deals.length > 0 || citySections.length > 0 ? (
        <>
          {/* Mobile: per-city carousels, each full viewport width (100vw breakout = edge-to-edge) */}
          <div className="md:hidden space-y-8">
            {citySections.map((section) => (
              <div key={section.city.code}>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 px-3">
                  From <span className="text-secondary font-semibold">{section.city.name}</span>
                </h3>
                <div className="w-screen relative left-1/2 -translate-x-1/2 overflow-x-auto scroll-smooth scrollbar-hide [max-width:100vw]">
                  <div className="flex gap-3 snap-x snap-mandatory pb-4 pl-3 pr-3">
                    {section.deals.map((deal, index) => (
                      <div
                        key={`${section.city.code}-${index}`}
                        className="snap-center shrink-0 w-[70%]"
                      >
                        <DealCard
                          deal={deal}
                          onClick={() => handleDealClick(deal)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tablet/desktop: city selector + grid of selected city's deals */}
          <div className="hidden md:block">
            {deals.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {deals.map((deal, index) => (
                  <DealCard
                    key={index}
                    deal={deal}
                    onClick={() => handleDealClick(deal)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        !loading && (
          <div className="text-center py-10">
            <p className="text-gray-600">No deals available at the moment. Check back soon!</p>
          </div>
        )
      )}

      {/* Flight Search Loading Modal */}
      {searchingFlights && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              {/* Animated Plane Icon */}
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <Plane className="w-16 h-16 text-secondary animate-bounce" />
                  <div className="absolute -right-2 -top-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-navy-400 rounded-full animate-ping"></div>
                      <div className="w-2 h-2 bg-navy-400 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
                      <div className="w-2 h-2 bg-navy-400 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Getting Cheap Flights
              </h3>
              
              {/* Progress Message */}
              <p className="text-gray-600 mb-6">
                {searchProgress}
              </p>
              
              {/* Loading Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-secondary to-navy-800 rounded-full animate-pulse"></div>
              </div>
              
              {/* Info Text */}
              <p className="text-sm text-gray-500 mt-4">
                Searching both Duffel and Amadeus for the best prices...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealsSection;
